import { Box, IconButton, Typography, Link, Chip } from '@mui/material';
import { useMemo } from 'react';
import linkIcon from '@/assets/icons/link.svg';
import sendIcon from '@/assets/icons/send.svg';
import fileIcon from '@/assets/icons/file.svg';

// 좌측 액션 타입 / Left action type
export type LeftAction =
  | { type: 'file'; onClick: () => void; active?: boolean; color?: string }
  | {
      type: 'custom';
      icon: string;
      label: string;
      onClick: () => void;
      active?: boolean;
      color?: string;
    };

// 공통 프롬프트 입력 컴포넌트 / Common prompt input component
export interface PromptComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isSending?: boolean;
  maxBytes?: number; // (legacy) 바이트 수 제한 / Byte count limit
  maxCharacters?: number; // 문자 수 제한 / Character count limit
  placeholder?: string;
  leftActions?: React.ReactNode; // (legacy) 좌측 추가 액션 슬롯
  rightActions?: React.ReactNode; // 전송 버튼 앞의 액션
  onFileUpload?: () => void; // (legacy) 파일 업로드 핸들러
  leftAction?: LeftAction; // 새 좌측 액션 API
  disableSend?: boolean; // 추가 비활성화 조건 / extra disable flag
  showCharacterCount?: boolean; // 문자 카운터 표시 여부 / Show character counter
  inputPadding?: string; // 입력 패딩 오버라이드 / Override input padding
  selectedFiles?: File[]; // 선택된 파일 목록 / Selected files list
  onRemoveFile?: (index: number) => void; // 파일 제거 핸들러 / File removal handler
}

export default function PromptComposer({
  value,
  onChange,
  onSend,
  isSending = false,
  maxBytes = 2000,
  maxCharacters = 3000,
  placeholder = '메시지를 입력하세요…',
  onFileUpload,
  leftAction,
  disableSend = false,
  showCharacterCount = false,
  inputPadding,
  selectedFiles = [],
  onRemoveFile,
}: PromptComposerProps) {
  // 바이트 계산 / Byte count calculation
  const byteCount = useMemo(() => new Blob([value || '']).size, [value]);
  const characterCount = useMemo(() => (value || '').length, [value]);
  const hasValue = (value || '').length > 0;
  
  // 문자 수 또는 바이트 수 제한 적용 / Apply character count or byte count limit
  const isOverLimit = maxCharacters !== undefined
    ? characterCount > maxCharacters 
    : byteCount > maxBytes;
    
  const disabled =
    disableSend || !value.trim() || isSending || isOverLimit;

  const resolveLeftColor = (active?: boolean, override?: string) =>
    active ? override || 'primary.main' : 'grey.500';

  return (
    <Box
      sx={{
        padding: '0px 12px 12px 12px',
        bgcolor: 'background.paper',
        borderRadius: '20px',
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            border: '2px solid',
            borderColor: 'grey.400',
            borderRadius: '16px',
            minHeight: selectedFiles.length > 0 ? 120 : 56,
            maxHeight: 200,
            position: 'relative',
            backgroundColor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* 선택된 파일 목록 - 입력창 내부 상단 / Selected files - Inside input top */}
          {selectedFiles.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 1,
                p: 1,
                overflow: 'hidden',
              }}
            >
              {selectedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: '14px',
                    px: 1.5,
                    py: 1,
                    gap: 1,
                    minWidth: 0,
                    flexShrink: 0,
                    width: '252px',
                    height: '55px',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: 'grey.500',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={fileIcon}
                      alt="file"
                      style={{
                        width: 16,
                        height: 16,
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      maxWidth: '161px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '12px',
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {file.name.length > 20
                        ? `${file.name.substring(0, 20)}...`
                        : file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '10px',
                        color: 'text.secondary',
                        lineHeight: 1.2,
                      }}
                    >
                      {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveFile?.(index)}
                    sx={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      p: 0.5,
                      minWidth: 'auto',
                      width: '24px',
                      height: '24px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '16px', color: 'text.primary' }}
                    >
                      ×
                    </Typography>
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* 텍스트 입력 영역 */}
          <Box
            sx={{
              flex: 1,
              padding: inputPadding || ' 20px',
              paddingBottom: '44px', // 버튼 영역 공간 확보
              minHeight: '56px',
              height:
                selectedFiles.length > 0
                  ? 'calc(200px - 44px - 60px)'
                  : 'calc(200px - 44px)', // 파일이 있을 때 높이 조정
              position: 'relative',
              '& textarea::placeholder': {
                fontWeight: 400,
              },
            }}
          >
            <textarea
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={e => {
                // Enter 키 처리 / Handle Enter key
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!disabled) {
                    onSend();
                  }
                }
              }}
              placeholder={placeholder}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontFamily: 'inherit',
                fontSize: '16px',
                fontWeight: hasValue ? 500 : 400,
                lineHeight: 1.5,
                backgroundColor: 'transparent',
                wordWrap: 'break-word',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0,0,0,0.2) transparent',
              }}
            />

            {/* 문자 카운터 - 입력창 내부 우측 하단 / Character counter - Inside input bottom right */}
            {showCharacterCount && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  zIndex: 10,
                }}
              >
                <Chip
                  size="small"
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isOverLimit ? 'error.main' : 'primary.main',
                          fontSize: '10px',
                          fontWeight: 'bold',
                        }}
                      >
                        {characterCount}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: '10px' }}
                      >
                        / {maxCharacters || `3,000`}자
                      </Typography>
                    </Box>
                  }
                  sx={{
                    height: 20,
                    fontSize: '10px',
                    bgcolor: 'grey.200',
                    borderColor: 'divider',
                    borderRadius: 8,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* 하단 행: 좌측 액션 + 전송 버튼 (입력창 안에 포함) / Bottom row: left action + send (inside input) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            zIndex: 20,
            backgroundColor: 'transparent', // 투명 배경으로 입력창과 통합
            padding: '8px 12px',
            borderRadius: '0 0 8px 8px',
            minHeight: '44px',
          }}
        >
          {/* 좌측 액션 렌더링: 우선 leftAction, 없으면 onFileUpload */}
          {leftAction ? (
            leftAction.type === 'custom' ? (
              <Link
                component="button"
                variant="caption"
                onClick={leftAction.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: resolveLeftColor(
                      leftAction.active,
                      leftAction.color
                    ),
                    mask: `url(${leftAction.icon}) no-repeat center / contain`,
                    WebkitMask: `url(${leftAction.icon}) no-repeat center / contain`,
                  }}
                />
                <Typography
                  component="span"
                  variant="subtitle3"
                  sx={{
                    color: resolveLeftColor(
                      leftAction.active,
                      leftAction.color
                    ),
                  }}
                >
                  {leftAction.label}
                </Typography>
              </Link>
            ) : (
              <Link
                component="button"
                variant="caption"
                onClick={leftAction.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: resolveLeftColor(
                      leftAction.active,
                      leftAction.color
                    ),
                    mask: `url(${linkIcon}) no-repeat center / contain`,
                    WebkitMask: `url(${linkIcon}) no-repeat center / contain`,
                  }}
                />
                <Typography
                  component="span"
                  variant="subtitle3"
                  sx={{
                    color: resolveLeftColor(
                      leftAction.active,
                      leftAction.color
                    ),
                  }}
                >
                  파일 추가
                </Typography>
              </Link>
            )
          ) : (
            onFileUpload && (
              <Link
                component="button"
                variant="caption"
                onClick={onFileUpload}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: 'grey.500',
                    mask: `url(${linkIcon}) no-repeat center / contain`,
                    WebkitMask: `url(${linkIcon}) no-repeat center / contain`,
                  }}
                />
                <Typography
                  component="span"
                  variant="subtitle3"
                  sx={{ color: 'grey.500' }}
                >
                  파일 추가
                </Typography>
              </Link>
            )
          )}

          <IconButton
            aria-label="send"
            onClick={onSend}
            disabled={disabled}
            sx={{
              ...(disabled
                ? {
                    backgroundColor: theme =>
                      `${theme.palette.grey[200]} !important`,
                    color: 'grey.600',
                    '&:hover': {
                      backgroundColor: theme =>
                        `${theme.palette.grey[200]} !important`,
                    },
                  }
                : {
                    backgroundColor: theme =>
                      `${theme.palette.primary.main} !important`,
                    color: 'common.white',
                    '&:hover': {
                      backgroundColor: theme =>
                        `${theme.palette.primary.dark} !important`,
                    },
                  }),
              width: 44,
              height: 44,
              borderRadius: '50%',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <img
              src={sendIcon}
              alt="Send"
              style={{
                width: 24,
                height: 24,
                filter: disabled ? 'none' : 'brightness(0) invert(1)',
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
